import { useEffect, useRef, useState } from "react";
import { CURRENCY_MAP } from "../../../../constants";
import { getEventDashboard } from "../../../../api/eventsApi";
import PageLoader from "../../../../components/pageLoader";

const DashboardTab = ({ event }) => {
  const [dashboard, setDashboard] = useState([]);
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(true);

  const currency = dashboard?.kpis?.revenue?.currency || "";
  const symbol = CURRENCY_MAP[currency] || "";

  //if (!dashboard) return null;

  const kpis = dashboard.kpis || {};
  const salesTrend = dashboard.sales_trend || {};
  const genderBreakdown = dashboard.gender_breakdown || {};
  const recentOrders = dashboard.recent_orders || [];

  const [activeRange, setActiveRange] = useState("7d");

  const [chartLoading, setChartLoading] = useState(false);

  const handleRangeChange = async (range) => {
    setActiveRange(range);
    try {
      setChartLoading(true);
      const data = await getEventDashboard(event.id, range);
      setDashboard((prev) => ({ ...prev, sales_trend: data.sales_trend }));
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail || err?.detail || "Something went wrong",
      });
    } finally {
      setChartLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString();
  };

  const fetchDashboardData = async (range = "7d") => {
    try {
      setLoading(true);
      const data = await getEventDashboard(event.id, range);
      setDashboard(data || {});
    } catch (err) {
      setToast({
        type: "error",
        message:
          err?.response?.data?.detail || err?.detail || "Something went wrong",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData("7d");
  }, []);

  if (loading) return <PageLoader />;

  return (
    <div className="space-y-6">
      {/* ── KPI Cards ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-10 gap-4">
        {/* Tickets Sold */}
        <div className="bg-slate-900/60 border border-white/10 rounded-xl py-5 px-4 col-span-3">
          <div className="flex justify-between">
            <div>
              <p className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-2">
                Tickets Sold
              </p>
              <div className="text-2xl font-semibold text-white flex items-baseline gap-1.5">
                {kpis.tickets_sold?.value ?? "—"}
                <span className="text-lg text-slate-500 font-normal">
                  / {kpis.tickets_sold?.total ?? "—"}
                </span>
              </div>
              {kpis.tickets_sold?.delta_pct != null && (
                <p
                  className={`text-xs mt-1 font-medium ${kpis.tickets_sold.delta_pct > 0 ? "text-emerald-400" : "text-red-400"}`}
                >
                  {kpis.tickets_sold.delta_pct > 0 ? "+" : ""}
                  {kpis.tickets_sold.delta_pct}%{" "}
                  {kpis.tickets_sold?.delta_label || ""}
                </p>
              )}
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">For Sold Out</p>
              <p className="text-sm text-slate-300">
                <span className="text-white font-medium">
                  {kpis.tickets_sold?.sold_out_rate ?? "—"}
                </span>{" "}
                / day
                {" ( "}
                <span className="text-white font-medium">
                  {kpis.tickets_sold?.days_to_event ?? "—"}
                </span>
                d left
                {" ) "}
              </p>
              <p className="text-sm text-slate-300 mt-2">
                Average Daily Sales: <br />
                <span className="text-white font-medium">
                  {kpis.tickets_sold?.avg_daily_orders ?? "—"}
                </span>{" "}
                Orders /{" ( "}
                <span className="text-white font-medium">
                  {kpis.tickets_sold?.avg_daily_tickets ?? "—"}
                </span>{" "}
                Tickets
                {" ) "}
              </p>
            </div>
          </div>

          {/* Today */}
          <div className="mt-3 pt-3 border-t border-white/5">
            <p className="text-xs text-slate-500 mb-1">Today Sales</p>
            <p className="text-sm text-slate-300">
              <span className="text-white font-medium">
                {kpis.tickets_sold?.today_tickets ?? "—"}
              </span>{" "}
              tickets
              {" / "}
              <span className="text-white font-medium">
                {kpis.tickets_sold?.today_orders ?? "—"}
              </span>{" "}
              orders
              {" / "}
              <span className="text-white font-medium">
                {`${symbol}${parseFloat(kpis.tickets_sold?.today_sales_value || 0).toFixed(2)}` ??
                  "—"}
              </span>{" "}
            </p>
          </div>

          {/* Order breakdown */}
          <div className="mt-3 grid grid-cols-3 gap-1">
            <div className="bg-slate-800/60 rounded-lg px-2 py-1.5 text-center">
              <p className="text-xs text-slate-500 mb-0.5">Started</p>
              <p className="text-sm font-medium text-white">
                {kpis.tickets_sold?.orders_started ?? "—"}
              </p>
            </div>
            <div className="bg-emerald-500/10 rounded-lg py-1.5 text-center">
              <p className="text-xs text-emerald-600 mb-0.5">Completed</p>
              <p className="text-sm font-medium text-emerald-400">
                {kpis.tickets_sold?.orders_completed ?? "—"}
                <span className="text-xs font-normal ml-0.5">
                  ({kpis.tickets_sold?.orders_completed_pct ?? 0}%)
                </span>
              </p>
            </div>
            <div className="bg-red-500/10 rounded-lg px-2 py-1.5 text-center">
              <p className="text-xs text-red-600 mb-0.5">Expired</p>
              <p className="text-sm font-medium text-red-400">
                {kpis.tickets_sold?.orders_expired ?? "—"}
                <span className="text-xs font-normal ml-0.5">
                  ({kpis.tickets_sold?.orders_expired_pct ?? 0}%)
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Revenue */}
        <div className="bg-slate-900/60 border border-white/10 rounded-xl py-5 px-4 col-span-3">
          <p className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-2">
            Revenue
          </p>
          <div className="text-2xl font-semibold text-white flex items-baseline gap-1.5 flex-wrap">
            {symbol}
            {parseFloat(kpis.revenue?.total || 0).toFixed(2)}
            <div className="relative group inline-block">
              <span className="text-lg text-slate-500 font-normal cursor-help">
                / {symbol}
                {parseFloat(kpis.revenue?.predicted_total || 0).toFixed(2)}
              </span>
              <div className="absolute z-50 bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 bg-slate-800 border border-white/10 text-xs text-slate-300 rounded-lg px-3 py-2 opacity-0 group-hover:opacity-100 transition pointer-events-none text-center">
                Predicted total revenue, based on the availability added
                <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" />
              </div>
            </div>
          </div>
          {kpis.revenue?.delta_pct != null && (
            <p
              className={`text-xs mt-1 font-medium ${kpis.revenue.delta_pct > 0 ? "text-emerald-400" : "text-red-400"}`}
            >
              {kpis.revenue.delta_pct > 0 ? "+" : ""}
              {kpis.revenue.delta_pct}% {kpis.revenue?.delta_label || ""}
            </p>
          )}
          <div>
            <div className="flex gap-4 mt-2">
              <div>
                <p className="text-xs text-slate-500">Tickets</p>
                <p className="text-sm text-slate-300">
                  {symbol}
                  {parseFloat(kpis.revenue?.ticket || 0).toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Commission</p>
                <p className="text-sm text-slate-300">
                  {symbol}
                  {parseFloat(
                    kpis.revenue?.commission_without_vat || 0,
                  ).toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-xs text-slate-500">VAT</p>
                <p className="text-sm text-slate-300">
                  {symbol}
                  {parseFloat(kpis.revenue?.commission_vat || 0).toFixed(2)}
                </p>
              </div>
            </div>
            <div className="flex gap-4 mt-2">
              <div>
                <p className="text-xs text-slate-500">Commission Total</p>
                <p className="text-sm text-slate-300">
                  {symbol}
                  {parseFloat(kpis.revenue?.commission_total || 0).toFixed(2)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-slate-900/60 border border-white/10 rounded-xl py-5 px-4 col-span-2">
          <p className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-2">
            Check-ins
          </p>
          <p className="text-2xl font-semibold text-white">
            {kpis.check_ins?.value ?? "—"}
          </p>
          {kpis.check_ins?.attendance_pct != null && (
            <p className="text-xs text-slate-500 mt-1">
              {kpis.check_ins.attendance_pct}% attendance
            </p>
          )}
        </div>

        {/* Refunds */}
        <div className="bg-slate-900/60 border border-white/10 rounded-xl py-5 px-4 col-span-2">
          <p className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-2">
            Refunds
          </p>
          <p
            className={`text-2xl font-semibold ${!kpis.refunds?.value ? "text-slate-500" : "text-white"}`}
          >
            {kpis.refunds?.value ?? "N/A"}
          </p>
          {kpis.refunds?.label && (
            <p className="text-xs text-slate-500 mt-1">{kpis.refunds.label}</p>
          )}
        </div>
      </div>

      {/* ── Sales Trend + Gender ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Sales Trend */}
        <div className="lg:col-span-2 bg-slate-900/60 border border-white/10 rounded-xl p-4">
          <div className="flex items-center justify-between mb-1">
            <div>
              <h3 className="text-sm font-semibold text-white">Sales Trend</h3>
              {/* <p className="text-xs text-slate-500 mt-0.5">
                Ticket purchases in the last {salesTrend.window_hours || 24} hours
              </p> */}
            </div>
            <span className="flex items-center gap-1.5 text-xs text-emerald-400 bg-emerald-500/10 px-2.5 py-1 rounded-full">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" />
              Live
            </span>
          </div>
          <SalesTrendChart
            salesTrend={salesTrend}
            activeRange={activeRange}
            onRangeChange={handleRangeChange}
            loading={chartLoading}
          />
        </div>

        {/* Gender Breakdown */}
        <div className="bg-slate-900/60 border border-white/10 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-white">Audience Gender</h3>
          <p className="text-xs text-slate-500 mt-0.5 mb-5">
            Distribution of paid orders
          </p>

          <GenderChart breakdown={genderBreakdown} />
        </div>
      </div>

      {/* ── Recent Orders ── */}
      <div className="bg-slate-900/60 border border-white/10 rounded-xl overflow-hidden">
        <div className="px-6 py-4 border-b border-white/10">
          <h3 className="text-sm font-semibold text-white">Recent Orders</h3>
          <p className="text-xs text-slate-500 mt-0.5">Latest 5 paid orders</p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-white/10 text-xs uppercase tracking-wider text-slate-400">
                <th className="px-6 py-3 text-left">Buyer</th>
                <th className="px-6 py-3 text-left">Ticket</th>
                <th className="px-6 py-3 text-center">QTY</th>
                <th className="px-6 py-3 text-center">Total</th>
                <th className="px-6 py-3 text-left">Order #</th>
                <th className="px-6 py-3 text-right">Time</th>
              </tr>
            </thead>
            <tbody>
              {recentOrders.map((order, idx) => (
                <tr
                  key={idx}
                  className="border-b border-white/5 last:border-none hover:bg-slate-800/40 transition"
                >
                  <td className="px-6 py-3 text-sm font-medium text-white">
                    {order.buyer_name || "—"}
                  </td>
                  <td className="px-6 py-3 text-sm text-slate-300">
                    {order.ticket_type_name || "—"}
                  </td>
                  <td className="px-6 py-3 text-sm text-slate-300 text-center">
                    {order.total_quantity ?? "—"}
                  </td>
                  <td className="px-6 py-3 text-sm text-slate-300 text-center">
                    {symbol}
                    {parseFloat(order.total || 0).toFixed(2)}
                  </td>
                  <td className="px-6 py-3 text-xs text-slate-400 font-mono">
                    {order.order_number || "—"}
                  </td>
                  <td className="px-6 py-3 text-xs text-slate-500 text-right">
                    {formatDate(order.created_at) || "—"}
                  </td>
                </tr>
              ))}

              {recentOrders.length === 0 && (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-10 text-center text-slate-400 text-sm"
                  >
                    No recent orders.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const SalesTrendChart = ({
  salesTrend,
  activeRange,
  onRangeChange,
  loading,
}) => {
  const [tooltip, setTooltip] = useState(null);

  const allBuckets = salesTrend.buckets || [];
  const ticketTypes = salesTrend.ticket_types || [];

  const COLORS = [
    "#f472b6",
    "#34d399",
    "#60a5fa",
    "#fb923c",
    "#a78bfa",
    "#facc15",
    "#f87171",
    "#2dd4bf",
    "#4ade80",
    "#e879f9",
    "#38bdf8",
    "#ff6b6b",
    "#fbbf24",
    "#818cf8",
    "#f97316",
    "#06b6d4",
    "#84cc16",
    "#ec4899",
    "#8b5cf6",
    "#22d3ee",
  ];

  const WINDOWS = [
    {
      key: "1d",
      label: "24h",
      format: (b) => {
        const d = new Date(b.hour || b.date || b.time || "");
        return isNaN(d.getTime())
          ? b.hour || b.date || ""
          : d.toLocaleTimeString("en-GB", {
              hour: "2-digit",
              minute: "2-digit",
            });
      },
      labelEvery: 2,
    },
    {
      key: "7d",
      label: "7 days",
      format: (b) => {
        const d = new Date(b.hour || b.date || b.time || "");
        return isNaN(d.getTime())
          ? b.hour || b.date || ""
          : d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
      },
      labelEvery: 1,
    },
    {
      key: "30d",
      label: "30 days",
      format: (b) => {
        const d = new Date(b.hour || b.date || b.time || "");
        return isNaN(d.getTime())
          ? b.hour || b.date || ""
          : d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
      },
      labelEvery: 3,
    },
  ];

  const activeWindow = WINDOWS.find((w) => w.key === activeRange) || WINDOWS[0];

  const now = Date.now();
  const cutoff = now - activeWindow.hours * 60 * 60 * 1000;

  const buckets = (salesTrend.buckets || []).map((b) => ({
    ...b,
    label: activeWindow.format(b),
  }));

  const allCounts = buckets.flatMap((b) =>
    ticketTypes.map((tt) => b.counts?.[tt.name] || 0),
  );
  const maxVal = Math.max(...allCounts, 1);

  const W = 560;
  const H = 180;
  const padL = 28;
  const padR = 12;
  const padT = 12;
  const padB = 36;
  const chartW = W - padL - padR;
  const chartH = H - padT - padB;

  const xPos = (i) => padL + (i / (buckets.length - 1 || 1)) * chartW;
  const yPos = (val) => padT + chartH - (val / maxVal) * chartH;

  const gridLines = [0, 1, 2, 3].map((v) => ({
    y: yPos((v / 3) * maxVal),
    label: Math.round((v / 3) * maxVal),
  }));

  const svgRef = useRef(null);

  const handleMouseMove = (e) => {
    if (!svgRef.current || !buckets.length) return;
    const rect = svgRef.current.getBoundingClientRect();
    const mouseX = ((e.clientX - rect.left) / rect.width) * W;

    let closestIdx = 0;
    let closestDist = Infinity;
    buckets.forEach((_, i) => {
      const dist = Math.abs(xPos(i) - mouseX);
      if (dist < closestDist) {
        closestDist = dist;
        closestIdx = i;
      }
    });

    setTooltip({
      svgX: xPos(closestIdx),
      clientX: e.clientX - rect.left,
      bucket: buckets[closestIdx],
    });
  };

  const handleMouseLeave = () => setTooltip(null);

  return (
    <div>
      {/* Window toggle */}
      <div className="flex items-center gap-1 mb-4">
        {WINDOWS.map((w) => (
          <button
            key={w.key}
            onClick={() => onRangeChange(w.key)}
            className={`px-3 py-1 rounded-md text-xs font-medium transition ${
              activeRange === w.key
                ? "bg-pink-600 text-white"
                : "text-slate-400 hover:text-white hover:bg-slate-800 border border-white/10"
            }`}
          >
            {w.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="h-48 flex items-center justify-center gap-3">
          <div className="w-5 h-5 border-2 border-white/10 border-t-pink-500 rounded-full animate-spin" />
          <span className="text-sm text-slate-500">Loading chart data...</span>
        </div>
      ) : !buckets.length ? (
        <div className="h-48 flex items-center justify-center text-slate-500 text-sm">
          No sales data available
        </div>
      ) : (
        <div className="relative select-none">
          <svg
            ref={svgRef}
            viewBox={`0 0 ${W} ${H}`}
            className="w-full cursor-crosshair"
            style={{ height: 200 }}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
          >
            {/* Grid lines */}
            {gridLines.map((g, i) => (
              <g key={i}>
                <line
                  x1={padL}
                  y1={g.y}
                  x2={W - padR}
                  y2={g.y}
                  stroke="rgba(255,255,255,0.06)"
                  strokeWidth="1"
                />
                <text
                  x={padL - 6}
                  y={g.y + 4}
                  textAnchor="end"
                  fontSize="10"
                  fill="#475569"
                >
                  {g.label}
                </text>
              </g>
            ))}

            {/* X axis baseline */}
            <line
              x1={padL}
              y1={padT + chartH}
              x2={W - padR}
              y2={padT + chartH}
              stroke="rgba(255,255,255,0.1)"
              strokeWidth="1"
            />

            {/* X axis ticks + labels */}
            {buckets.map((b, i) => {
              if (i % activeWindow.labelEvery !== 0) return null;
              const x = xPos(i);
              return (
                <g key={i}>
                  <line
                    x1={x}
                    y1={padT + chartH}
                    x2={x}
                    y2={padT + chartH + 4}
                    stroke="rgba(255,255,255,0.15)"
                    strokeWidth="1"
                  />
                  <text
                    x={x}
                    y={padT + chartH + 14}
                    textAnchor="middle"
                    fontSize="10"
                    fill="#475569"
                  >
                    {b.label}
                  </text>
                </g>
              );
            })}

            {/* Lines + dots */}
            {/* Lines + dots */}
            {ticketTypes.map((tt, ti) => {
              const color = COLORS[ti % COLORS.length];

              // Build smooth curved path using cubic bezier
              const points = buckets.map((b, i) => ({
                x: xPos(i),
                y: yPos(b.counts?.[tt.name] || 0),
              }));

              const d = points.reduce((acc, point, i) => {
                if (i === 0) return `M ${point.x} ${point.y}`;
                const prev = points[i - 1];
                const cpX = (prev.x + point.x) / 2;
                return `${acc} C ${cpX} ${prev.y}, ${cpX} ${point.y}, ${point.x} ${point.y}`;
              }, "");

              return (
                <g key={ti}>
                  <path
                    d={d}
                    fill="none"
                    stroke={color}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    opacity={tooltip ? 0.35 : 1}
                  />
                  {buckets.map((b, i) => {
                    const val = b.counts?.[tt.name] || 0;
                    if (!val) return null;
                    return (
                      <circle
                        key={i}
                        cx={xPos(i)}
                        cy={yPos(val)}
                        r="2.5"
                        fill={color}
                        opacity={tooltip ? 0.25 : 1}
                      />
                    );
                  })}
                </g>
              );
            })}

            {/* Hover vertical line */}
            {tooltip && (
              <line
                x1={tooltip.svgX}
                y1={padT}
                x2={tooltip.svgX}
                y2={padT + chartH}
                stroke="rgba(255,255,255,0.25)"
                strokeWidth="1"
                strokeDasharray="3,3"
              />
            )}

            {/* Highlighted dots on hovered bucket */}
            {tooltip &&
              ticketTypes.map((tt, ti) => {
                const val = tooltip.bucket.counts?.[tt.name] || 0;
                if (!val) return null;
                const color = COLORS[ti % COLORS.length];
                return (
                  <circle
                    key={tt.name}
                    cx={tooltip.svgX}
                    cy={yPos(val)}
                    r="5"
                    fill={color}
                    stroke="#0f172a"
                    strokeWidth="2"
                  />
                );
              })}
          </svg>

          {/* Tooltip */}
          {tooltip &&
            (() => {
              const entries = ticketTypes
                .map((tt, ti) => ({
                  name: tt.name,
                  val: tooltip.bucket.counts?.[tt.name] || 0,
                  color: COLORS[ti % COLORS.length],
                }))
                .filter((e) => e.val > 0);

              if (!entries.length) return null;

              const isRightHalf = tooltip.clientX > 280;
              const total = entries.reduce((sum, e) => sum + e.val, 0);

              return (
                <div
                  className="absolute z-50 pointer-events-none"
                  style={{
                    top: "50%",
                    left: isRightHalf
                      ? `calc(${(tooltip.svgX / W) * 100}% - 8px)`
                      : `calc(${(tooltip.svgX / W) * 100}% + 8px)`,
                    transform: isRightHalf
                      ? "translate(-100%, -50%)"
                      : "translate(0%, -50%)",
                  }}
                >
                  <div className="bg-slate-900 border border-white/15 rounded-xl shadow-2xl px-3 py-2.5 min-w-[150px] max-w-[200px]">
                    <p className="text-xs text-slate-400 font-medium pb-1.5 mb-1.5 border-b border-white/10">
                      {tooltip.bucket.label}
                    </p>
                    <div className="space-y-1 max-h-48 overflow-y-auto">
                      {entries.map((e) => (
                        <div
                          key={e.name}
                          className="flex items-center justify-between gap-2"
                        >
                          <div className="flex items-center gap-1.5 min-w-0">
                            <span
                              className="w-2 h-2 rounded-full flex-shrink-0"
                              style={{ backgroundColor: e.color }}
                            />
                            <span className="text-xs text-slate-400 truncate">
                              {e.name}
                            </span>
                          </div>
                          <span className="text-xs font-semibold text-white flex-shrink-0">
                            {e.val}
                          </span>
                        </div>
                      ))}
                    </div>
                    {entries.length > 1 && (
                      <div className="flex justify-between pt-1.5 mt-1 border-t border-white/10">
                        <span className="text-xs text-slate-500">Total</span>
                        <span className="text-xs font-semibold text-white">
                          {total}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}
        </div>
      )}

      {/* Legend */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
        {ticketTypes.map((tt, ti) => (
          <div key={ti} className="flex items-center gap-1.5">
            <span
              className="inline-block w-3 h-0.5 rounded"
              style={{ backgroundColor: COLORS[ti % COLORS.length] }}
            />
            <span className="text-xs text-slate-400">{tt.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ── Gender Donut Chart (SVG) ── */
const GenderChart = ({ breakdown }) => {
  const total = breakdown.total || 0;
  const segments = breakdown.segments || [];

  const COLORS = ["#1e293b", "#94a3b8", "#f472b6", "#a78bfa"];

  if (!total) {
    return (
      <div className="h-40 flex items-center justify-center text-slate-500 text-sm">
        No data
      </div>
    );
  }

  const R = 60;
  const cx = 80;
  const cy = 80;
  const strokeW = 20;

  let cumulative = 0;
  const arcs = segments.map((seg, i) => {
    const pct = seg.count / total;
    const startAngle = cumulative * 2 * Math.PI - Math.PI / 2;
    cumulative += pct;
    const endAngle = cumulative * 2 * Math.PI - Math.PI / 2;

    const x1 = cx + R * Math.cos(startAngle);
    const y1 = cy + R * Math.sin(startAngle);
    const x2 = cx + R * Math.cos(endAngle);
    const y2 = cy + R * Math.sin(endAngle);
    const largeArc = pct > 0.5 ? 1 : 0;

    return {
      d: `M ${x1} ${y1} A ${R} ${R} 0 ${largeArc} 1 ${x2} ${y2}`,
      color: COLORS[i % COLORS.length],
      seg,
    };
  });

  return (
    <div className="flex flex-col items-center gap-4">
      <svg viewBox="0 0 160 160" className="w-40 h-40">
        {arcs.map((arc, i) => (
          <path
            key={i}
            d={arc.d}
            fill="none"
            stroke={arc.color}
            strokeWidth={strokeW}
            strokeLinecap="butt"
          />
        ))}
        <text
          x={cx}
          y={cy - 8}
          textAnchor="middle"
          fontSize="22"
          fontWeight="600"
          fill="#f1f5f9"
        >
          {total}
        </text>
        <text
          x={cx}
          y={cy + 10}
          textAnchor="middle"
          fontSize="10"
          fill="#64748b"
        >
          ATTENDEES
        </text>
      </svg>

      <div className="w-full space-y-2">
        {segments.map((seg, i) => (
          <div key={i} className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span
                className="w-3 h-3 rounded-full inline-block"
                style={{ backgroundColor: COLORS[i % COLORS.length] }}
              />
              <span className="text-sm text-slate-400 capitalize">
                {seg.gender || "Unspecified"}
              </span>
            </div>
            <span className="text-sm text-slate-300">
              {seg.count} ({seg.percentage}%)
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DashboardTab;
